import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useState } from "react";
import { buildSkillSlug, Skill } from "@/v2/entities/skill/Skill.model";
import { toast } from "sonner";
import { Dialog } from "@/components/shared/dialog";
import { useSkillApi } from "@/v2/entities/skill/Skill.api";
import { useMutation } from "react-query";
import styles from "./skills.module.scss";

interface EditSkillProps {
  skill: Skill;
  refetch: () => void;
  onSuccess: () => void;
  onDiscard: () => void;
}

export function EditSkill({
  skill,
  refetch,
  onSuccess,
  onDiscard,
}: EditSkillProps) {
  const { updateSkill } = useSkillApi();

  const { mutate: updateSkillMutation } = useMutation({
    mutationKey: ["updateSkill"],
    mutationFn: updateSkill,
    onSuccess: () => {
      toast("Skill updated successfully");
      refetch();
      onSuccess();
    },
    onError: () => {
      toast.error("Failed to update skill");
    },
  });

  const [skillPayload, setSkillPayload] = useState(skill);

  const handleUpdateSkill = useCallback(async () => {
    updateSkillMutation(skillPayload);
  }, [skillPayload, updateSkillMutation, refetch]);

  return (
    <Dialog open>
      <Dialog.Content
        isPortal={true}
        size="large"
        className="w-[800px] max-h-[90vh] flex flex-col overflow-hidden"
        style={{ height: "90vh" }}
      >
        <div className="flex-shrink-0 border-b border-gray-200">
          <Dialog.Title>Edit Skill</Dialog.Title>
          <Text size="2" mt="4" ml="2">
            Update the details of your skill.
          </Text>
        </div>
        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            <div
              className={`flex-1 ${styles.skillsModalScrollArea}`}
              style={{ minHeight: 0 }}
            >
              <Flex direction="column" gap="3" p="4">
                <label>
                  <Text as="div" size="2" mb="1" weight="bold">
                    Name
                  </Text>
                  <input
                    value={skillPayload.name}
                    onChange={(e) => {
                      setSkillPayload({
                        ...skillPayload,
                        name: e.target.value,
                        slug: buildSkillSlug(e.target.value),
                      });
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>

                <label>
                  <Text as="div" size="2" mb="1" weight="bold">
                    Description
                  </Text>
                  <textarea
                    value={skillPayload.description}
                    onChange={(e) =>
                      setSkillPayload({
                        ...skillPayload,
                        description: e.target.value,
                      })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>

                <label>
                  <Text as="div" size="2" mb="1" weight="bold">
                    cURL
                  </Text>
                  <textarea
                    rows={8}
                    value={skillPayload.curl ?? ""}
                    onChange={(e) =>
                      setSkillPayload({
                        ...skillPayload,
                        curl: e.target.value,
                      })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>
              </Flex>
            </div>
          </div>
        </div>
        <div className={`flex-shrink-0 p-4 ${styles.floatingActions}`}>
          <Flex gap="3" justify="end">
            <Button variant="soft" color="gray" onClick={onDiscard}>
              Cancel
            </Button>

            <Button color="gray" highContrast onClick={handleUpdateSkill}>
              Update Skill
            </Button>
          </Flex>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
